import React, { ReactElement, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ZDrawer from "@molecules/ZDrawer";
import ZTable from "@organisms/ZTable";
import ZModal from "@atoms/ZModal";
import Form from "antd/lib/form";
import ZForm from "@organisms/ZForm";
import ZCheckbox from "@atoms/ZCheckbox";
import ConfigProvider from "antd/lib/config-provider";
import EmptyBundleProductsIcon from "@/Assets/ProductsPage/EmptyBundleProductsIcon.svg";
import ProductPricingPlanDrawer from "@pages/Drawers/Products/PricingPlan";
import AddTaxCategoryDrawer from "@pages/TaxListPage/Drawers/TaxCategory";
import { GlobalState } from "@/Reducers/interface";
import {
	BundleProduct,
	ProductType,
	ProductBillingType,
	ProductPricingPlan,
	Product,
	EditOrCloneProductDrawerMode,
	ProductSource
} from "@/Contracts/Products";
import { PricingPlanNames, UserRole } from "@/Contracts/Users";
import EditBundleChildProductDrawer from "@pages/Drawers/Products/BundleToChildProduct/Edit";
import AddBundleChildToProductDrawer from "@pages/Drawers/Products/BundleToChildProduct/Add";
import ZMessage from "@molecules/ZMessage";
import { IAddProductDrawerProps, EProductDrawerState } from "./interface";
import { useFeatureAccess } from "@/Hooks/useFeatureAccess";
import { IFormProps, ICustomFormItemProps } from "@organisms/ZForm/interface";
import {
	ProductItemPricing,
	BundledProductTableColumns
} from "@templates/ZDrawers/ZProducts";
import { capitalizeFirstLetter } from "@/Contracts/Utils";
import useCommonFunctions from "@/Hooks/Products/useProductDrawerCommonFunctions";
import useAddProduct from "@/Hooks/Products/useAddProduct";
import useEditOrCloneProduct from "@/Hooks/Products/useEditOrCloneProduct";
import ZAddSupplier from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier";
import SettingsActionCreators from "@/Actions/Settings";
import useDispatch from "@/Components/Utils/useDispatch";
import { SupplierFormValues } from "@templates/ZSuppliers/ZDrawers/ZAddOrEditSupplier/interface";
import { useAddOrEditSupplier } from "@/Hooks/Supplier/useAddOrEditSupplier";
import { useFeatureFlag } from "@/Hooks/useFeature";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { FormKeys } from "@/Contracts";
import { useBundlesSettings } from "@/Hooks";
import { Switch } from "antd";
import ZTypography from "@atoms/ZTypography";
import ZIcon from "@atoms/ZIcon";
import { EIcons } from "@atoms/ZIcon/interface";
import ProductsActionCreators from "@/Actions/Products";
import { getProductDetails } from "@/Services/Products";
import useSuperOpsCrudLockout from "@/Hooks/useSuperOpsCrudLockout";
import ZAlert from "@atoms/ZAlert";

const { ZText } = ZTypography;

const AddProductDrawer: React.FC<IAddProductDrawerProps> = props => {
	const [form] = Form.useForm();
	const isAdminOrManager = useFeatureAccess(
		PricingPlanNames.IGNITE,
		UserRole.SecondaryRole
	);
	const isLocked = useSuperOpsCrudLockout();

	const {
		isLoading,
		selectedEtilizeProduct,
		currentActiveDocument,
		addTaxCategoryDrawerOpen,
		addOrEditSupplierDrawerOpen,
		isFormEdited,
		userCompany
	} = useSelector((state: GlobalState) => ({
		isLoading: state.products.isLoading,
		selectedEtilizeProduct: state.products.selectedEtilizeProduct,
		currentActiveDocument: state.documents.currentActiveDocument,
		addTaxCategoryDrawerOpen: state.user.addTaxCategoryDrawerOpen,
		addOrEditSupplierDrawerOpen: state.settings.addOrEditSupplierDrawerOpen,
		isFormEdited: state.settings.isFormEdited,
		userCompany: state.user.userCompany
	}));

	const dispatch = useDispatch();

	const { addSupplier, isSubmitting, getSuppliers, getSupplierById } =
		useAddOrEditSupplier();
	const enable_bundles_enchancements = useFeatureFlag(
		"enable_bundles_enchancements"
	);

	const getCurrentActiveProduct = () => {
		return props.drawerState === EProductDrawerState.ETILIZEADD
			? selectedEtilizeProduct
			: props.currentActiveProduct;
	};

	const currentActiveProduct = getCurrentActiveProduct();

	const isAmazonProduct =
		!!userCompany.integrations.amazon_business?.connected_user?.length &&
		(currentActiveProduct?.product_source === ProductSource.Amazon ||
			currentActiveProduct?.id?.includes(ProductSource.Amazon) ||
			(!!currentActiveProduct?.asin &&
				currentActiveProduct?.asin !== "NA"));

	const pricingID =
		isAmazonProduct && props.drawerState !== EProductDrawerState.ETILIZEADD
			? currentActiveProduct?.pricing[0]?.id ?? null
			: props.productVsPricing && selectedEtilizeProduct
			? props?.productVsPricing[selectedEtilizeProduct?.id]
			: null;

	const pricing = pricingID
		? selectedEtilizeProduct?.pricing.find(
				pricing => pricingID === pricing.id
		  )
		: null;

	const [
		addProductPricingPlanDrawerOpen,
		setAddProductPricingPlanDrawerOpen
	] = useState<boolean>(false);
	const [bundledProductList, setBundledProductList] = useState<
		BundleProduct[]
	>([]);
	const [
		currentActiveProductPricingPlan,
		setCurrentActiveProductPricingPlan
	] = useState<ProductPricingPlan | null>(null);
	const [editChildBundleItem, setEditChildBundleItem] =
		useState<BundleProduct | null>(null);
	const [
		editProductPricingPlanDrawerOpen,
		setEditProductPricingPlanDrawerOpen
	] = useState<boolean>(false);
	const [searchProductsDrawerOpen, setSearchProductsDrawerOpen] =
		useState<boolean>(false);
	const [productPricingPlanList, setProductPricingPlanList] = useState<
		ProductPricingPlan[]
	>([]);
	const [supplierIdForPricingPlan, setSupplierIdForPricingPlan] =
		useState<string>();

	const {
		pricingDisabled,
		showChildItems,
		showQuantity,
		showPrice,
		setShowChildItems,
		setShowQuantity,
		setShowPrice,
		handleChangeChildItems,
		handleChangeQuantity,
		handleChangePrice
	} = useBundlesSettings();

	const handleBundleRowChange = (
		productId: string,
		data: Partial<BundleProduct>
	) => {
		setBundledProductList(prevState => [
			...prevState.map(a =>
				a.item_id === productId ? { ...a, ...data } : a
			)
		]);
	};

	const handleBundledProductSellPriceChange =
		(productId: string) => (sell_price?: number | string | null) => {
			if (typeof sell_price === "undefined" || sell_price === null) {
				return;
			}

			handleBundleRowChange(productId, {
				sell_price: Number(sell_price)
			});
		};

	const handleBundledProductCostPriceChange =
		(productId: string) =>
		(selected_cost_price?: number | string | null) => {
			if (
				typeof selected_cost_price === "undefined" ||
				selected_cost_price === null
			) {
				return;
			}
			handleBundleRowChange(productId, {
				selected_cost_price: Number(selected_cost_price)
			});
		};

	const handleBundledProductQuantityChange =
		(productId: string) => (quantity?: number | string | null) => {
			if (!quantity) {
				return;
			}

			handleBundleRowChange(productId, { quantity: Number(quantity) });
		};

	const handleBundledProductSetupPriceChange =
		(productId: string) =>
		(selected_setup_price?: number | string | null) => {
			if (
				typeof selected_setup_price === "undefined" ||
				selected_setup_price === null
			) {
				return;
			}

			handleBundleRowChange(productId, {
				selected_setup_price: Number(selected_setup_price)
			});
		};

	const handlePricingChange =
		(selectedVendorProductId: string) => (newPricingId: string) => {
			const selectedProduct = bundledProductList.find(
				a => a.item_id === selectedVendorProductId
			);

			const pricingList = selectedProduct?.item?.pricing;
			if (!pricingList?.length) {
				throw new Error(
					`There are no pricing in this product to change`
				);
			}

			const selectedPricingData = pricingList?.find(
				a => a.id === newPricingId
			);
			if (!selectedPricingData) {
				throw new Error(
					`Invalid Pricing Id: expected one of ${pricingList
						?.map(a => a.id)
						.join(", ")}, received ${newPricingId}`
				);
			}
			handleBundleRowChange(selectedVendorProductId, {
				selected_pricing_id: newPricingId,
				selected_cost_price: selectedPricingData?.cost_price,
				selected_setup_price: selectedPricingData?.setup_price,
				selected_billing_period:
					selectedPricingData?.billing_period ||
					ProductBillingType.OneTime,
				sell_price: selectedPricingData?.sell_price
			});
		};

	const handleBundleProductChildDelete = (currProductId: string) => () => {
		setBundledProductList(prevState => [
			...prevState.filter(i => i.item_id !== currProductId)
		]);
	};

	const handleEditDrawerOpen =
		(updatedEditChildBundleItem: BundleProduct) => () =>
			setEditChildBundleItem(updatedEditChildBundleItem);

	const handleEditDrawerSubmit = async (productRequest: BundleProduct) => {
		// check if the product already exists
		const alreadyExists = bundledProductList.find(
			product => product.item_id === productRequest.item_id
		);

		// if yes, append to the list
		if (!alreadyExists) {
			// old image list to be appended with bundled product images
			const updatedProductImageList: string[] = [...productImageList];
			const adddedBundledProductList = [
				...bundledProductList,
				productRequest
			];
			const updatedBundledProductList = !!adddedBundledProductList.length
				? adddedBundledProductList.map((product, index) => {
						return {
							...product,
							index
						};
				  })
				: [];
			setBundledProductList(updatedBundledProductList);
			setProductImageList([...updatedProductImageList]);
		} else {
			// else, update existing product
			const { ...bundledProductData } = productRequest;
			handleBundleRowChange(productRequest.item_id, bundledProductData);
		}

		setEditChildBundleItem(null);

		// get cost price, sell price and setup fee
		const values = getFieldValues();
		const {
			show_individual_product_pricing,
			sell_price,
			selected_cost_price,
			selected_setup_price
		} = values;

		// check if it has bundle level pricing
		if (show_individual_product_pricing) {
			// update the pricing
			form.setFieldsValue({
				selected_cost_price:
					selected_cost_price + productRequest.selected_cost_price,
				sell_price: sell_price + productRequest.sell_price,
				selected_setup_price:
					selected_setup_price + productRequest.selected_setup_price
			});
		}
	};

	const handleEditDrawerClose = () => setEditChildBundleItem(null);

	const toggleBundleProductOptional = (product: BundleProduct) => () =>
		handleBundleRowChange(product.item_id, {
			is_optional: !product.is_optional
		});

	const getBundledProductTableColumns = () =>
		BundledProductTableColumns({
			handlePricingChange: handlePricingChange,
			handleBundledProductSetupPriceChange:
				handleBundledProductSetupPriceChange,
			handleBundledProductCostPriceChange:
				handleBundledProductCostPriceChange,
			handleBundledProductSellPriceChange:
				handleBundledProductSellPriceChange,
			handleBundledProductQuantityChange:
				handleBundledProductQuantityChange,
			handleBundleProductChildDelete: handleBundleProductChildDelete,
			handleEditDrawerOpen: handleEditDrawerOpen,
			toggleBundleProductOptional: toggleBundleProductOptional,
			isReadonly:
				(currentProduct?.read_only &&
					props.mode === EditOrCloneProductDrawerMode.EDIT) ??
				false
		});

	const resetState = () => {
		resetPartialState();
		setBundledProductList([]);
		setProductPricingPlanList([]);
		setCurrentActiveProductPricingPlan(null);
		setCurrentPricingPlanEditIndex(null);
		setAddProductPricingPlanDrawerOpen(false);
		setEditProductPricingPlanDrawerOpen(false);
		setSearchProductsDrawerOpen(false);
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddEditCloneProduct)
		);
	};

	const customEmptyBundleProductsTable = () => {
		return (
			<div className="flex flex-col items-center justify-center">
				<EmptyBundleProductsIcon className="mb-3" />
				<div className="mb-2">
					You have no products to display in this bundle.
				</div>
				<div>
					Click on 'Add Product' button to add a product to this
					bundle
				</div>
			</div>
		);
	};

	const showAddProductPricingPlanDrawer = () =>
		setAddProductPricingPlanDrawerOpen(true);

	const closeAddProductPricingPlanDrawer = () =>
		setAddProductPricingPlanDrawerOpen(false);

	const [currentPricingPlanEditIndex, setCurrentPricingPlanEditIndex] =
		useState<number | null>();

	const showEditProductPricingPlanDrawer = (
		$event: any,
		productPricingPlan: ProductPricingPlan,
		index: number
	) => {
		stopPropagation($event.domEvent);
		setCurrentActiveProductPricingPlan(productPricingPlan);
		setCurrentPricingPlanEditIndex(index);
		setEditProductPricingPlanDrawerOpen(true);
	};

	const closeEditProductPricingPlanDrawer = () => {
		setCurrentActiveProductPricingPlan(null);
		setCurrentPricingPlanEditIndex(null);
		setEditProductPricingPlanDrawerOpen(false);
	};

	const showSearchProductsDrawer = () => setSearchProductsDrawerOpen(true);

	const closeSearchProductsDrawer = () => setSearchProductsDrawerOpen(false);

	const deleteProductPricingPlan = (
		$event: any,
		deleteSelectedProductPricingPlan: ProductPricingPlan
	) => {
		stopPropagation($event.domEvent);
		ZModal.confirm({
			title: "Are you sure you want to delete this pricing plan?",
			onOk: () => {
				if (!!deleteSelectedProductPricingPlan?.id?.length) {
					setProductPricingPlanList(prevState => [
						...prevState.filter(
							productPricingPlan =>
								productPricingPlan.id !==
								deleteSelectedProductPricingPlan.id
						)
					]);
				} else if (
					!!deleteSelectedProductPricingPlan?.session_id?.length
				) {
					setProductPricingPlanList(prevState => [
						...prevState.filter(
							productPricingPlan =>
								productPricingPlan.session_id !==
								deleteSelectedProductPricingPlan.session_id
						)
					]);
				}
			},
			okType: "danger",
			okText: "Delete"
		});
	};

	const addProductPricingPlan = (
		addSelectedProductPricingPlan: ProductPricingPlan
	) => {
		const isPlanNameInValid = productPricingPlanList?.find((v, index) => {
			if (currentPricingPlanEditIndex === index) {
				return false;
			}
			return (
				v.name.trim().toLowerCase() ===
				addSelectedProductPricingPlan.name.trim().toLowerCase()
			);
		});

		if (isPlanNameInValid) {
			ZMessage.error("Pricing plan name must be unique");
			return;
		}

		setProductPricingPlanList([
			...productPricingPlanList.concat(addSelectedProductPricingPlan)
		]);
		setAddProductPricingPlanDrawerOpen(false);
		ZMessage.success("Pricing plan added successfully");
	};

	const handleProductPricingPlanChange = (
		editSelectedProductPricingPlan: ProductPricingPlan
	) => {
		const isPlanNameInValid = productPricingPlanList?.find((v, index) => {
			if (currentPricingPlanEditIndex === index) {
				return false;
			}
			return (
				v.name.trim().toLowerCase() ===
				editSelectedProductPricingPlan.name.trim().toLowerCase()
			);
		});

		if (isPlanNameInValid) {
			ZMessage.error("Pricing plan name must be unique");
			return;
		}

		let updatedProductPricingPlanList = [...productPricingPlanList];
		if (!!editSelectedProductPricingPlan?.id?.length) {
			updatedProductPricingPlanList = productPricingPlanList.map(
				productPricingPlan =>
					productPricingPlan.id === editSelectedProductPricingPlan.id
						? editSelectedProductPricingPlan
						: productPricingPlan
			);
		} else if (!!editSelectedProductPricingPlan?.session_id?.length) {
			updatedProductPricingPlanList = productPricingPlanList.map(
				productPricingPlan =>
					productPricingPlan.session_id ===
					editSelectedProductPricingPlan.session_id
						? editSelectedProductPricingPlan
						: productPricingPlan
			);
		}
		setProductPricingPlanList([...updatedProductPricingPlanList]);
		setEditProductPricingPlanDrawerOpen(false);
		setCurrentActiveProductPricingPlan(null);
		setCurrentPricingPlanEditIndex(null);
		ZMessage.success("Pricing plan edited successfully");
	};

	const onClose = () => {
		resetState();
		props.onClose && props.onClose();
		dispatch(
			SettingsActionCreators.resetFormEdited(FormKeys.AddEditCloneProduct)
		);
	};

	const handleCancel = () => {
		if (!isFormEdited?.[FormKeys.AddEditCloneProduct]) {
			onClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard
					${
						EProductDrawerState.ADD === props.drawerState ||
						props.drawerState === EProductDrawerState.ETILIZEADD ||
						EProductDrawerState.ADDAMAZON === props.drawerState
							? "adding"
							: props.mode === "edit"
							? "editing"
							: "cloning"
					} the product?`,
				onOk: onClose
			});
		}
	};

	const hideAddSupplierDrawer = () =>
		dispatch(SettingsActionCreators.hideAddOrEditSupplierDrawer());

	const onSupplierAdd = async (supplierFormValues: SupplierFormValues) => {
		const supplier = await addSupplier(supplierFormValues);
		await getSupplierById(supplier?.id);

		if (
			addProductPricingPlanDrawerOpen ||
			editProductPricingPlanDrawerOpen ||
			searchProductsDrawerOpen ||
			!!editChildBundleItem
		) {
			setSupplierIdForPricingPlan(supplier?.id);
		} else form.setFieldsValue({ supplier_id: supplier?.id });

		return supplier;
	};

	const handleDragEnd = ({ ...props }) => {
		const { oldIndex, newIndex } = props;
		const newBundledProducts = [...bundledProductList];
		const destinationIndex = newIndex ? newIndex : 0;
		const sourceIndex = oldIndex ? oldIndex : 0;
		const [removed] = newBundledProducts.splice(sourceIndex, 1);
		newBundledProducts.splice(destinationIndex, 0, removed);

		setBundledProductList(newBundledProducts);
	};

	const ProductBundle = () => (
		<ConfigProvider renderEmpty={customEmptyBundleProductsTable}>
			<ZTable
				loading={isLoading}
				scroll={{ x: true, scrollToFirstRowOnChange: true }}
				size="small"
				columns={getBundledProductTableColumns()}
				dataSource={bundledProductList}
				pagination={false}
				className="mb-8 border-solid border-0 border-b-2 border-gray-5"
				rowKey="item_id"
				footer={() => {
					return (
						<span
							className="link flex justify-center border-0 botder-t-1 border-solid border-gray-5"
							onClick={showSearchProductsDrawer}
						>
							Add Product
						</span>
					);
				}}
				isDraggable={false}
				onDragEnd={handleDragEnd}
				draggableRowInDrawers={true}
			/>
			{enable_bundles_enchancements && (
				<div className="mb-6 mt-6">
					<ZText className="mr-4">Show child items</ZText>
					<Switch
						onChange={handleChangeChildItems}
						size="small"
						checked={showChildItems}
					/>
					{showChildItems && (
						<>
							<div className="flex items-center mt-1">
								<ZIcon
									name={EIcons.INFOCIRCLE}
									size="sm"
									className="m-0"
								/>
								<ZText
									colorVariant={8}
									level={0}
									className="ml-2"
								>
									Please note that you will not be able to
									edit the product bundle pricing fields if
									the{" "}
									<ZText colorVariant={9} level={0}>
										Show Price
									</ZText>{" "}
									is toggled on.
								</ZText>
							</div>
							<div className="ml-9 mt-4">
								<ZText className="mr-4">Show quantity</ZText>
								<Switch
									onChange={handleChangeQuantity}
									size="small"
									checked={showQuantity}
								/>
								<div className="mb-2"></div>
								<ZText className="mr-9">Show price</ZText>
								<Switch
									onChange={() => {
										handleChangePrice();
										setShowIndividualProductPricing(
											!showPrice
										);
									}}
									size="small"
									checked={showPrice}
								/>
							</div>
						</>
					)}
				</div>
			)}
		</ConfigProvider>
	);

	const bundleSettingDetail = {
		showChildItems,
		showQuantity,
		showPrice
	};

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditCloneProduct] &&
			dispatch(
				SettingsActionCreators.setFormEdited(
					FormKeys.AddEditCloneProduct
				)
			);
	};

	const ProductPricing: ReactElement = ProductItemPricing({
		isLoading,
		showAddProductPricingPlanDrawer,
		productPricingPlanList,
		showEditProductPricingPlanDrawer,
		deleteProductPricingPlan
	});

	const {
		sections,
		getFieldValues,
		currentProduct,
		closeAddTaxCategoryDrawer,
		handleTaxCategoryDrawerOnSubmit,
		setCurrentProductType,
		customFieldList,
		productImageList,
		stopPropagation,
		resetPartialState,
		setProductImageList,
		setCurrentProduct,
		showIndividualProductPricing,
		setShowIndividualProductPricing,
		resetDynamicForm,
		selectedDistributor,
		selectedPricing
	} = useCommonFunctions(
		form,
		props.drawerState,
		ProductPricing,
		bundledProductList,
		ProductBundle,
		productPricingPlanList,
		props.mode,
		undefined,
		pricing,
		props.visible,
		pricingDisabled
	);

	const onAddSubmit = useAddProduct(
		productPricingPlanList,
		bundledProductList,
		productImageList,
		customFieldList,
		resetState,
		selectedEtilizeProduct,
		selectedDistributor,
		selectedPricing,
		props.shouldRedirectToProduct,
		props.onAdd,
		bundleSettingDetail,
		enable_bundles_enchancements
	);

	const { onEditOrCloneSubmit, TemplateModal } = useEditOrCloneProduct(
		productPricingPlanList,
		bundledProductList,
		productImageList,
		customFieldList,
		currentProduct,
		showIndividualProductPricing,
		props.drawerState,
		selectedDistributor,
		selectedPricing,
		props.mode,
		onClose,
		bundleSettingDetail,
		enable_bundles_enchancements
	);

	const getAmazonProductDetails = async function () {
		const currentProduct = getCurrentActiveProduct();
		const amazonProductId = currentProduct?.id.includes(
			ProductSource.Amazon
		)
			? currentProduct.id
			: currentProduct?.asin
			? ProductSource.Amazon + ":" + currentProduct?.asin
			: null;
		if (!amazonProductId) {
			return;
		}
		try {
			ProductsActionCreators.showProductsLoader();
			const itemResponse = await getProductDetails(
				amazonProductId,
				ProductSource.Amazon
			);
			dispatch(
				ProductsActionCreators.setSelectedEtilizeProduct(itemResponse)
			);
			initializeStates(itemResponse);
		} catch (e) {
			console.error(e);
		} finally {
			ProductsActionCreators.hideProductsLoader();
		}
	};

	useEffect(() => {
		if (
			props.visible &&
			isAmazonProduct &&
			props.drawerState === EProductDrawerState.ETILIZEADD
		) {
			getAmazonProductDetails();
		}
	}, [
		props.drawerState,
		props.visible,
		currentActiveProduct?.id,
		isAmazonProduct
	]);

	function initializeStates(updatedProductDetails?: Product) {
		let curProduct: Product | null = null;
		let curProductType: ProductType = ProductType.Software;
		if (props.drawerState === EProductDrawerState.ETILIZEADD) {
			curProduct = updatedProductDetails ?? selectedEtilizeProduct;
			curProductType = props.defaultProductType ?? ProductType.Software;
		} else if (props.drawerState === EProductDrawerState.EDITORCLONE) {
			curProduct = props.currentActiveProduct ?? null;
			curProductType =
				props.currentActiveProduct?.type ?? ProductType.Software;
		}
		const curProductImageList = curProduct?.item_images ?? [];
		setProductImageList([...curProductImageList]);
		setProductPricingPlanList(
			curProduct?.pricing?.filter(
				item =>
					!item.distributor_pricing_details &&
					!item.amazon_business_pricing_details
			) ?? []
		);
		const bundleProductDataSource = !!curProduct?.bundled_items?.length
			? curProduct.bundled_items.map((product, index) => {
					return {
						...product,
						index
					};
			  })
			: [];
		setBundledProductList(bundleProductDataSource);
		setCurrentProduct(curProduct);
		setCurrentProductType(curProductType);
		setShowIndividualProductPricing(
			curProduct?.show_individual_product_pricing ?? false
		);
		if (enable_bundles_enchancements) {
			setShowChildItems(
				curProduct?.show_child_items_in_quote_block ?? false
			);
			setShowQuantity(curProduct?.show_child_quantity ?? false);
			setShowPrice(curProduct?.show_individual_product_pricing ?? false);
		}
	}

	useEffect(() => {
		if (props.visible) {
			initializeStates();
		} else {
			resetState();
		}
	}, [
		props.visible,
		selectedEtilizeProduct,
		props.currentActiveProduct,
		enable_bundles_enchancements
	]);

	useEffect(() => {
		if (props.visible) {
			getSuppliers();
		}
	}, [props.visible]);

	useEffect(() => {
		if (props.visible) {
			let selectedSupplierId: string | undefined;
			if (props.currentActiveProduct) {
				const { supplier_id } = getFieldValues();
				selectedSupplierId = supplier_id;
			} else if (editProductPricingPlanDrawerOpen) {
				selectedSupplierId =
					currentActiveProductPricingPlan?.supplier_id as string;
			}

			if (selectedSupplierId) {
				getSupplierById(selectedSupplierId);
			}
		}
	}, [editProductPricingPlanDrawerOpen, props.currentActiveProduct]);

	useEffect(() => resetDynamicForm(), [currentProduct]);

	if (
		props.mode === EditOrCloneProductDrawerMode.EDIT &&
		!currentProduct?.read_only
	) {
		sections.push({
			isFooterSection: true,
			meta: {
				fields: [
					{
						key: "update_existing_templates",
						initialValue: false,
						widget: ({ value, onChange }: ICustomFormItemProps) => (
							<div className="inline-flex items-center">
								<ZCheckbox
									onChange={e => onChange(e.target.checked)}
									checked={value}
								>
									Update Values in existing templates
								</ZCheckbox>
							</div>
						)
					}
				]
			}
		});
	}
	const formProps: IFormProps = {
		onFinish:
			props.drawerState === EProductDrawerState.EDITORCLONE
				? onEditOrCloneSubmit
				: onAddSubmit,
		key: "ProductForm",
		sections,
		loading: isLoading,
		submitButtonText: "Save",
		isStickFooter: true,
		form,
		isCancelAllowed: true,
		onCancel: handleCancel,
		isRightFooter: true,
		isDynamic: true,
		scrollToFirstError: true,
		shouldDisableBeforeTouch:
			props.drawerState === EProductDrawerState.ADD ||
			props.drawerState === EProductDrawerState.ADDAMAZON,
		disabled:
			((currentProduct?.read_only &&
				props.mode === EditOrCloneProductDrawerMode.EDIT) ||
				isLocked) ??
			false,
		shouldResetFormOnCancel: false,
		onValuesChange: handleFormChange
	};

	const getDrawerTitle = () => {
		switch (props.drawerState) {
			case EProductDrawerState.EDITORCLONE:
				return `${capitalizeFirstLetter(props.mode)} Product`;
			case EProductDrawerState.ADDAMAZON:
				return "Add Amazon Product";
			default:
				return "Add Product";
		}
	};
	return (
		<ZDrawer
			visible={props.visible}
			closable={true}
			destroyOnClose={true}
			onClose={handleCancel}
			title={getDrawerTitle()}
			removeDefaultPadding={true}
		>
			<div onClick={e => e.stopPropagation()}>
				{currentActiveProductPricingPlan ? (
					<ProductPricingPlanDrawer
						visible={editProductPricingPlanDrawerOpen}
						onClose={closeEditProductPricingPlanDrawer}
						isLoading={isLoading}
						onSubmit={handleProductPricingPlanChange}
						currentActiveProductPricingPlan={
							currentActiveProductPricingPlan
						}
						isEditMode
						supplierId={supplierIdForPricingPlan}
					/>
				) : (
					<ProductPricingPlanDrawer
						visible={addProductPricingPlanDrawerOpen}
						onClose={closeAddProductPricingPlanDrawer}
						isLoading={isLoading}
						onSubmit={addProductPricingPlan}
						supplierId={supplierIdForPricingPlan}
					/>
				)}
				{props.drawerState !== EProductDrawerState.BUNDLECHILDEDIT && (
					<AddBundleChildToProductDrawer
						visible={searchProductsDrawerOpen}
						onClose={closeSearchProductsDrawer}
						onSubmit={handleEditDrawerSubmit}
						currentActiveDocument={currentActiveDocument}
						isFromProductsPage={true}
						bundledProductList={bundledProductList}
						supplierId={supplierIdForPricingPlan}
					/>
				)}
				{props.drawerState !== EProductDrawerState.BUNDLECHILDEDIT &&
					editChildBundleItem && (
						<EditBundleChildProductDrawer
							currentActiveProduct={editChildBundleItem}
							visible={!!editChildBundleItem}
							onSubmit={handleEditDrawerSubmit}
							onClose={handleEditDrawerClose}
							currentActiveDocument={currentActiveDocument}
							bundledProductList={bundledProductList}
							supplierId={supplierIdForPricingPlan}
						/>
					)}
				{isAdminOrManager && (
					<>
						{isLocked ? (
							<ZAlert
								type="info"
								showIcon
								message="Product management is handled by your SuperOps integration and cannot be edited here."
								className="mx-6 mt-4"
							/>
						) : (
							(props.drawerState === EProductDrawerState.ADD ||
								props.drawerState ===
									EProductDrawerState.ADDAMAZON ||
								currentProduct) && (
								<ZForm {...formProps} className="py-4" />
							)
						)}
						{props.drawerState ===
							EProductDrawerState.EDITORCLONE && TemplateModal}
						<AddTaxCategoryDrawer
							visible={addTaxCategoryDrawerOpen}
							onClose={closeAddTaxCategoryDrawer}
							handleTaxCategoryDrawerOnSubmit={
								handleTaxCategoryDrawerOnSubmit
							}
						/>
						<ZAddSupplier
							visible={addOrEditSupplierDrawerOpen}
							closeDrawer={hideAddSupplierDrawer}
							addSupplier={onSupplierAdd}
							isFormLoading={isSubmitting}
						/>
					</>
				)}
			</div>
		</ZDrawer>
	);
};

export default AddProductDrawer;
